import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";

// ─── useCountUp ───────────────────────────────────────────────────────────────
function useCountUp(target: number, duration = 1000) {
  const [count, setCount] = useState(0);
  const raf = useRef<number>(0);
  useEffect(() => {
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setCount(Math.round(ease * target));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return count;
}

// ─── Button ───────────────────────────────────────────────────────────────────
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost" | "outline";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  icon?: React.ReactNode;
}

export function Button({ variant = "primary", size = "md", loading = false, icon, children, className, disabled, ...props }: ButtonProps) {
  const base = "inline-flex items-center justify-center gap-2 font-medium rounded-xl transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97]";
  const variants = {
    primary:   "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-500 shadow-sm",
    secondary: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-gray-400",
    danger:    "bg-red-500 text-white hover:bg-red-600 focus:ring-red-400 shadow-sm",
    ghost:     "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-300",
    outline:   "border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-300 shadow-sm",
  };
  const sizes = { sm: "text-xs px-3 py-1.5", md: "text-sm px-4 py-2", lg: "text-base px-5 py-2.5" };
  return (
    <button className={cn(base, variants[variant], sizes[size], className)} disabled={disabled || loading} {...props}>
      {loading ? (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : icon}
      {children}
    </button>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
interface BadgeProps {
  variant?: "green" | "red" | "orange" | "blue" | "gray" | "purple";
  dot?: boolean;
  pulse?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function Badge({ variant = "gray", dot = false, pulse = false, children, className }: BadgeProps) {
  const variants = {
    green:  "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-600/20 dark:ring-green-500/30",
    red:    "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-600/20 dark:ring-red-500/30",
    orange: "bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 ring-orange-600/20 dark:ring-orange-500/30",
    blue:   "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-600/20 dark:ring-blue-500/30",
    gray:   "bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ring-gray-500/20 dark:ring-gray-500/30",
    purple: "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ring-purple-600/20 dark:ring-purple-500/30",
  };
  const dotColors = {
    green: "bg-green-500", red: "bg-red-500", orange: "bg-orange-500",
    blue: "bg-blue-500", gray: "bg-gray-400", purple: "bg-purple-500",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset", variants[variant], className)}>
      {(dot || pulse) && (
        <span className="relative flex h-1.5 w-1.5">
          {pulse && <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", dotColors[variant])} />}
          <span className={cn("relative inline-flex rounded-full h-1.5 w-1.5", dotColors[variant])} />
        </span>
      )}
      {children}
    </span>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  glow?: boolean;
  padding?: "sm" | "md" | "lg" | "none";
}

export function Card({ children, className, hover = false, glow = false, padding = "md" }: CardProps) {
  const paddings = { none: "", sm: "p-4", md: "p-6", lg: "p-8" };
  return (
    <motion.div
      whileHover={hover ? { y: -2, boxShadow: "0 8px 30px rgba(0,0,0,0.12)" } : undefined}
      transition={{ duration: 0.2 }}
      className={cn(
        "bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-card relative overflow-hidden transition-colors duration-200",
        glow && "after:absolute after:inset-0 after:rounded-2xl after:ring-1 after:ring-inset after:ring-brand-500/10",
        hover && "cursor-pointer",
        paddings[padding],
        className,
      )}
    >
      {children}
    </motion.div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  title:    string;
  value:    string | number;
  icon:     React.ReactNode;
  iconBg:   string;
  trend?:   { value: number; label: string };
  subtitle?: string;
  delay?:   number;
}

export function StatCard({ title, value, icon, iconBg, trend, subtitle, delay = 0 }: StatCardProps) {
  const isNum = typeof value === "number";
  const counted = useCountUp(isNum ? (value as number) : 0, 900);
  const display = isNum ? counted : value;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      whileHover={{ y: -3, boxShadow: "0 12px 32px rgba(0,0,0,0.12)" }}
      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-card p-6 relative overflow-hidden transition-all duration-200"
    >
      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-[0.06] bg-current blur-2xl pointer-events-none" />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white tracking-tight tabular-nums">
            {display}
          </p>
          {subtitle && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
          {trend && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: delay + 0.5 }}
              className={cn("mt-2 text-xs font-medium flex items-center gap-1", trend.value >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400")}
            >
              <span>{trend.value >= 0 ? "↑" : "↓"}</span>
              <span>{Math.abs(trend.value)}% {trend.label}</span>
            </motion.p>
          )}
        </div>
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: delay + 0.15, type: "spring", stiffness: 200 }}
          className={cn("p-3 rounded-xl", iconBg)}
        >
          {icon}
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Animated Progress Bar ────────────────────────────────────────────────────
interface ProgressBarProps {
  value: number;
  color?: string;
  delay?: number;
}

export function ProgressBar({ value, color = "bg-indigo-500", delay = 0 }: ProgressBarProps) {
  return (
    <div className="mt-3 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <motion.div
        className={cn("h-full rounded-full", color)}
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(value * 100, 100)}%` }}
        transition={{ duration: 0.8, delay, ease: "easeOut" }}
      />
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
interface AvatarProps {
  name: string;
  src?: string;
  size?: "sm" | "md" | "lg";
  status?: "online" | "offline" | "busy";
}

export function Avatar({ name, src, size = "md", status }: AvatarProps) {
  const sizes = { sm: "w-8 h-8 text-xs", md: "w-10 h-10 text-sm", lg: "w-12 h-12 text-base" };
  const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const statusColors = { online: "bg-green-400", offline: "bg-gray-300", busy: "bg-orange-400" };
  return (
    <div className="relative inline-flex">
      {src ? (
        <img src={src} alt={name} className={cn("rounded-full object-cover", sizes[size])} />
      ) : (
        <div className={cn("rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 font-semibold flex items-center justify-center", sizes[size])}>
          {initials}
        </div>
      )}
      {status && (
        <span className={cn("absolute bottom-0 right-0 block rounded-full ring-2 ring-white dark:ring-gray-800", size === "sm" ? "w-2 h-2" : "w-2.5 h-2.5", statusColors[status])} />
      )}
    </div>
  );
}
